import React, { useCallback, useEffect, useState } from 'react'
import { gql } from 'graphql-request'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { AlertTriangle, ArrowDown, ArrowUp, Check, ChevronDown, ChevronRight, Plus, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { OrganisationWithDetails } from '../index'
import MinutePackagesPanel, { CURRENCIES, type MinutePackage } from './MinutePackagesPanel'
import { PAYMENT_ACCOUNT_FIELDS, type PaymentAccount } from '@/pages/PaymentAccounts'
import type { CardContent } from '@/components/PlanCard'
import { GET_DEFAULT_CARD, GET_TEMPLATES, type PlanCardTemplate } from '@/pages/PlanCards/editing'
import PlanCardPanel, { type PlanCardFields } from './PlanCardPanel'
import PaywallContentPanel from './PaywallContentPanel'

/**
 * A B2C organisation's subscription plans and the package catalogue they draw on.
 *
 * A plan is what a user must hold to use the app: a recurring price, the modules it
 * opens, and one included package whose minutes each paid period grants (its own price
 * is ignored there). What a plan charges and grants never changes after creation; to
 * change it, deactivate the plan and add another. Existing subscribers keep theirs.
 */

interface SubscriptionPlan {
  id: number
  name: string
  priceAmount: number
  currency: string
  interval: 'month' | 'quarter' | 'year'
  /** Editable, unlike the rest of the billing fields; applies to new subscriptions. */
  renews: boolean
  includedPackageId: number
  includedPackageName: string
  includedSeconds: number
  moduleIds: number[]
  moduleNames: string[]
  isActive: boolean
  sortOrder: number
  subscriberCount: number
  cardTemplateId: number | null
  cardTemplateName: string | null
  cardOverrides: PlanCardFields['cardOverrides']
  card: CardContent
}

interface B2cModuleOption {
  moduleId: number
  moduleName: string
  isActive: boolean
}

const PLAN_FIELDS = `
  id
  name
  priceAmount
  currency
  interval
  renews
  includedPackageId
  includedPackageName
  includedSeconds
  moduleIds
  moduleNames
  isActive
  sortOrder
  subscriberCount
  cardTemplateId
  cardTemplateName
  cardOverrides
  card
`

const GET_PLANS = gql`
  query getSubscriptionPlans($organisationId: Int!) {
    getSubscriptionPlans(organisationId: $organisationId) {
      ${PLAN_FIELDS}
    }
  }
`

const GET_B2C_MODULES = gql`
  query getB2cModulesForPlans($organisationId: Int!) {
    getB2cModules(organisationId: $organisationId) {
      moduleId
      moduleName
      isActive
    }
  }
`

const GET_PAYMENT_ACCOUNTS = gql`
  query getPaymentAccountsForPlans {
    getPaymentAccounts {
      ${PAYMENT_ACCOUNT_FIELDS}
    }
  }
`

const CREATE_PLAN = gql`
  mutation createSubscriptionPlan($input: CreateSubscriptionPlanInput!) {
    createSubscriptionPlan(input: $input) {
      ${PLAN_FIELDS}
    }
  }
`

const UPDATE_PLAN = gql`
  mutation updateSubscriptionPlan($input: UpdateSubscriptionPlanInput!) {
    updateSubscriptionPlan(input: $input) {
      ${PLAN_FIELDS}
    }
  }
`

const toMinorUnits = (price: string): number | null => {
  const value = Number(price)
  if (price.trim() === '' || !Number.isFinite(value) || value <= 0) return null
  return Math.round(value * 100)
}

const money = (amount: number, currency: string) => `${(amount / 100).toFixed(2)} ${currency.toUpperCase()}`

const errorMessage = (error: any, fallback: string) => error?.response?.errors?.[0]?.message || fallback

const PlansTab: React.FC<{ organisation: OrganisationWithDetails | null; loading: boolean }> = ({ organisation, loading }) => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([])
  const [modules, setModules] = useState<B2cModuleOption[]>([])
  const [packages, setPackages] = useState<MinutePackage[]>([])
  const [paymentAccount, setPaymentAccount] = useState<PaymentAccount | null>(null)
  const [loadingPlans, setLoadingPlans] = useState(false)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [names, setNames] = useState<Record<number, string>>({})
  const [templates, setTemplates] = useState<PlanCardTemplate[]>([])
  const [defaultCard, setDefaultCard] = useState<CardContent | null>(null)
  /** The plan whose card panel is open. */
  const [cardPlanId, setCardPlanId] = useState<number | null>(null)

  const [newName, setNewName] = useState('')
  const [newPrice, setNewPrice] = useState('')
  const [newCurrency, setNewCurrency] = useState('eur')
  const [newInterval, setNewInterval] = useState<'month' | 'quarter' | 'year'>('month')
  const [newRenews, setNewRenews] = useState(true)
  const [newModuleIds, setNewModuleIds] = useState<number[]>([])
  const [newPackageId, setNewPackageId] = useState('')
  const [adding, setAdding] = useState(false)

  const organisationId = organisation?.id

  const fetchAll = useCallback(async () => {
    if (!organisationId) return
    try {
      setLoadingPlans(true)
      const [planResponse, moduleResponse] = await Promise.all([
        authorizedGraphQLRequest<{ getSubscriptionPlans: SubscriptionPlan[] }>(GET_PLANS, { organisationId }),
        authorizedGraphQLRequest<{ getB2cModules: B2cModuleOption[] }>(GET_B2C_MODULES, { organisationId }),
      ])
      setPlans(planResponse.getSubscriptionPlans || [])
      setModules(moduleResponse.getB2cModules || [])
      setNames({})
    } catch (err: any) {
      setError(errorMessage(err, 'Failed to load plans'))
    } finally {
      setLoadingPlans(false)
    }
  }, [organisationId])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  // Card designs are global; archived ones too, since a plan may keep one.
  useEffect(() => {
    Promise.all([
      authorizedGraphQLRequest<{ getPlanCardTemplates: PlanCardTemplate[] }>(GET_TEMPLATES, { includeArchived: true }),
      authorizedGraphQLRequest<{ getDefaultPlanCard: CardContent }>(GET_DEFAULT_CARD),
    ])
      .then(([templateResponse, defaultResponse]) => {
        setTemplates(templateResponse.getPlanCardTemplates || [])
        setDefaultCard(defaultResponse.getDefaultPlanCard)
      })
      .catch((err) => console.error('Failed to load plan card designs:', err))
  }, [])

  useEffect(() => {
    const attachedId = organisation?.paymentAccountId
    if (!attachedId) {
      setPaymentAccount(null)
      return
    }
    let cancelled = false
    authorizedGraphQLRequest<{ getPaymentAccounts: PaymentAccount[] }>(GET_PAYMENT_ACCOUNTS)
      .then((response) => {
        if (cancelled) return
        const account = (response.getPaymentAccounts || []).find((a) => a.id === attachedId) ?? null
        setPaymentAccount(account)
        if (account?.defaultCurrency) setNewCurrency(account.defaultCurrency.toLowerCase())
      })
      .catch((err) => console.error('Failed to load the payment account:', err))
    return () => {
      cancelled = true
    }
  }, [organisation?.paymentAccountId])

  const replacePlan = (updated: SubscriptionPlan) => {
    setPlans((current) => current.map((p) => (p.id === updated.id ? updated : p)).sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id))
    setNames((current) => {
      const { [updated.id]: _saved, ...rest } = current
      return rest
    })
  }

  const update = async (input: Record<string, unknown>) => {
    setError(null)
    try {
      setBusyId(Number(input.id))
      const response = await authorizedGraphQLRequest<{ updateSubscriptionPlan: SubscriptionPlan }>(UPDATE_PLAN, { input })
      replacePlan(response.updateSubscriptionPlan)
    } catch (err: any) {
      setError(errorMessage(err, 'Failed to update the plan'))
    } finally {
      setBusyId(null)
    }
  }

  const move = async (index: number, direction: -1 | 1) => {
    const target = index + direction
    if (target < 0 || target >= plans.length) return
    const a = plans[index]
    const b = plans[target]
    await update({ id: a.id, sortOrder: b.sortOrder === a.sortOrder ? target : b.sortOrder })
    await update({ id: b.id, sortOrder: index })
  }

  const handleAdd = async () => {
    if (!organisationId) return
    setError(null)
    const priceAmount = toMinorUnits(newPrice)
    if (priceAmount === null) {
      setError('The price must be greater than zero.')
      return
    }
    if (newModuleIds.length === 0) {
      setError('Choose at least one module.')
      return
    }
    if (!newPackageId) {
      setError('Choose the package whose minutes the plan includes.')
      return
    }
    try {
      setAdding(true)
      const response = await authorizedGraphQLRequest<{ createSubscriptionPlan: SubscriptionPlan }>(CREATE_PLAN, {
        input: {
          organisationId,
          name: newName.trim(),
          priceAmount,
          currency: newCurrency,
          interval: newInterval,
          renews: newRenews,
          moduleIds: newModuleIds,
          includedPackageId: Number(newPackageId),
          sortOrder: plans.length,
        },
      })
      setPlans((current) => [...current, response.createSubscriptionPlan])
      setNewName('')
      setNewPrice('')
      setNewModuleIds([])
      setNewPackageId('')
      setNewRenews(true)
    } catch (err: any) {
      setError(errorMessage(err, 'Failed to create the plan'))
    } finally {
      setAdding(false)
    }
  }

  if (loading || (loadingPlans && plans.length === 0)) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!organisation) {
    return <div className="text-center text-gray-500 py-12">No organisation selected</div>
  }

  const activeModules = modules.filter((m) => m.isActive)
  const includable = packages.filter((p) => p.isActive)
  const activePlans = plans.filter((p) => p.isActive).length
  const paymentsUsable = organisation.paymentsUsable === true

  return (
    <div className="space-y-6">
      {(activePlans > 0 || packages.some((p) => p.isActive && p.soldSeparately)) && !paymentsUsable && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>
            This organisation has plans or top-ups on sale but no usable payment account, so nobody can subscribe or buy. Attach one in the Settings tab, or create one under Payment accounts.
          </span>
        </div>
      )}
      {activePlans === 0 && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>No active plan: people in this organisation cannot use the app until there is one to subscribe to.</span>
        </div>
      )}

      {error && <div className="rounded-lg bg-red-50 border border-red-100 p-2 text-sm text-red-700">{error}</div>}

      <div>
        <h4 className="font-semibold text-gray-800 mb-3">Plans ({plans.length})</h4>
        {plans.length === 0 ? (
          <div className="text-center text-gray-500 py-8 bg-gray-50 rounded-xl">
            <Sparkles className="h-10 w-10 mx-auto mb-2 text-gray-300" />
            <p>No plans yet</p>
          </div>
        ) : (
          <ul className="space-y-2">
            {plans.map((plan, index) => {
              const draft = names[plan.id]
              const dirty = draft !== undefined && draft.trim() !== plan.name
              const busy = busyId === plan.id
              return (
                <li key={plan.id} className={`rounded-xl border border-gray-200 bg-white p-4 ${plan.isActive ? '' : 'opacity-60'}`}>
                  <div className="flex items-center gap-2">
                    <input
                      value={draft ?? plan.name}
                      onChange={(e) => setNames((current) => ({ ...current, [plan.id]: e.target.value }))}
                      className="flex-1 min-w-0 px-2 py-1 border border-gray-200 rounded text-sm font-medium focus:ring-1 focus:ring-primary focus:border-transparent"
                    />
                    <Button size="sm" variant="outline" disabled={!dirty || busy} onClick={() => update({ id: plan.id, name: (draft ?? '').trim() })}>
                      <Check className="h-3.5 w-3.5" />
                    </Button>
                    <button type="button" disabled={busy || index === 0} onClick={() => move(index, -1)} className="p-1 rounded hover:bg-gray-100 text-gray-400 disabled:opacity-30" title="Move up">
                      <ArrowUp className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      disabled={busy || index === plans.length - 1}
                      onClick={() => move(index, 1)}
                      className="p-1 rounded hover:bg-gray-100 text-gray-400 disabled:opacity-30"
                      title="Move down"
                    >
                      <ArrowDown className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div className="mt-2 grid gap-1 text-xs text-gray-600 md:grid-cols-2">
                    <span className="tabular-nums">
                      {money(plan.priceAmount, plan.currency)} per {plan.interval} · {Math.round(plan.includedSeconds / 60)} min each {plan.interval} ({plan.includedPackageName})
                      {plan.renews ? null : <span className="ml-1.5 rounded bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-800">does not renew</span>}
                    </span>
                    <span className="truncate">Modules: {plan.moduleNames.join(', ')}</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <span className="flex items-center gap-3 text-gray-500">
                      {plan.subscriberCount} active subscribers
                      <button
                        type="button"
                        onClick={() => setCardPlanId((current) => (current === plan.id ? null : plan.id))}
                        className="inline-flex items-center gap-0.5 text-primary hover:underline"
                      >
                        {cardPlanId === plan.id ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                        Card: {plan.cardTemplateName ?? 'Default'}
                        {plan.cardOverrides ? ' (customised)' : ''}
                      </button>
                    </span>
                    <span className="flex items-center gap-3">
                      <label className="inline-flex items-center gap-1.5 text-gray-600" title="Applies to subscriptions started after the change. Existing subscribers are not affected.">
                        <input type="checkbox" checked={plan.renews} disabled={busy} onChange={(e) => update({ id: plan.id, renews: e.target.checked })} />
                        Renews automatically
                      </label>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => {
                          if (plan.isActive && !confirm(`Stop offering "${plan.name}"? Current subscribers keep it until they cancel.`)) return
                          update({ id: plan.id, isActive: !plan.isActive })
                        }}
                        className="text-primary hover:underline disabled:opacity-50"
                      >
                        {plan.isActive ? 'Deactivate' : 'Reactivate'}
                      </button>
                    </span>
                  </div>
                  {cardPlanId === plan.id && (
                    <PlanCardPanel
                      key={plan.id}
                      planId={plan.id}
                      plan={plan}
                      current={plan}
                      templates={templates}
                      defaultCard={defaultCard}
                      onSaved={(fields) => setPlans((current) => current.map((p) => (p.id === plan.id ? { ...p, ...fields } : p)))}
                    />
                  )}
                </li>
              )
            })}
          </ul>
        )}

        <div className="mt-4 rounded-xl border border-gray-200 p-4">
          <h5 className="font-semibold text-gray-800 mb-1">Add a plan</h5>
          <p className="text-xs text-gray-500 mb-3">
            Price, interval, modules and included package cannot be changed later; whether the plan renews can. Unused minutes do not carry over to the next period. Subscribers of a renewing plan
            cancel in Stripe's portal and keep it to the end of the period; a plan that does not renew ends by itself after one period.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-40 flex-1">
              <label className="block text-[11px] font-medium text-gray-600 mb-1">Name</label>
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Pro monthly"
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-1">Price</label>
              <input
                type="number"
                min={0}
                step={0.01}
                value={newPrice}
                onChange={(e) => setNewPrice(e.target.value)}
                placeholder="29.00"
                className="w-24 px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-1">Currency</label>
              <select value={newCurrency} onChange={(e) => setNewCurrency(e.target.value)} className="px-2 py-1.5 border border-gray-300 rounded text-sm bg-white uppercase">
                {CURRENCIES.map((code) => (
                  <option key={code} value={code}>
                    {code.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-1">Every</label>
              <select value={newInterval} onChange={(e) => setNewInterval(e.target.value as 'month' | 'quarter' | 'year')} className="px-2 py-1.5 border border-gray-300 rounded text-sm bg-white">
                <option value="month">month</option>
                <option value="quarter">quarter</option>
                <option value="year">year</option>
              </select>
            </div>
            <label className="inline-flex items-center gap-1.5 pb-2 text-sm text-gray-700" title="Can be changed later; applies to new subscriptions.">
              <input type="checkbox" checked={newRenews} onChange={(e) => setNewRenews(e.target.checked)} />
              Renews automatically
            </label>
            <div className="min-w-48">
              <label className="block text-[11px] font-medium text-gray-600 mb-1">Included minutes (package)</label>
              <select value={newPackageId} onChange={(e) => setNewPackageId(e.target.value)} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm bg-white">
                <option value="">Select a package</option>
                {includable.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} · {Math.round(p.seconds / 60)} min
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-3">
            <label className="block text-[11px] font-medium text-gray-600 mb-1">Modules</label>
            {activeModules.length === 0 ? (
              <p className="text-xs text-gray-500">Add a module in the Modules tab first.</p>
            ) : (
              <div className="flex flex-wrap gap-3">
                {activeModules.map((m) => (
                  <label key={m.moduleId} className="inline-flex items-center gap-1.5 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={newModuleIds.includes(m.moduleId)}
                      onChange={(e) => setNewModuleIds((current) => (e.target.checked ? [...current, m.moduleId] : current.filter((id) => id !== m.moduleId)))}
                    />
                    {m.moduleName}
                  </label>
                ))}
              </div>
            )}
          </div>
          <Button className="mt-4" size="sm" onClick={handleAdd} disabled={adding || !newName.trim() || !newPrice.trim()}>
            <Plus className="h-3.5 w-3.5 mr-1" />
            {adding ? 'Adding…' : 'Add plan'}
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 p-4">
        <PaywallContentPanel organisationId={organisation.id} plans={plans.filter((p) => p.isActive)} />
      </div>

      <div className="rounded-xl border border-gray-200 p-4">
        <MinutePackagesPanel organisationId={organisation.id} defaultCurrency={paymentAccount?.defaultCurrency} onPackagesChange={setPackages} />
      </div>
    </div>
  )
}

export default PlansTab
